import { useRef } from "react"
import { QRCodeCanvas, QRCodeSVG } from "qrcode.react"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faDownload, faEye } from "@fortawesome/free-solid-svg-icons"
import CopyIcon from "./CopyIcon"
import { getEventQrLink, getEventQrPreviewLink } from "../utils/eventQr"

// Big enough for a full-screen slide without blurring.
const PNG_SIZE = 1024

/*
  The event name printed under the QR in the downloads, so several codes made
  for different events can't be mixed up once they're files. Sized relative
  to the QR (4% of its width) so it stays small at any export size.
*/
const CAPTION_RATIO = 0.04
const CAPTION_COLOR = "#6b7280"
const CAPTION_FONT = "Arial, Helvetica, sans-serif"

const captionContext = (fontSize) => {
  const context = document.createElement("canvas").getContext("2d")
  context.font = `${fontSize}px ${CAPTION_FONT}`
  return context
}

// Shortens a long event name with an ellipsis so it never runs past the QR.
const fitCaption = (context, text, maxWidth) => {
  if (context.measureText(text).width <= maxWidth) return text
  let fitted = text
  while (
    fitted.length > 1 &&
    context.measureText(`${fitted}…`).width > maxWidth
  ) {
    fitted = fitted.slice(0, -1)
  }
  return `${fitted.trimEnd()}…`
}

const escapeXml = (text) =>
  text.replace(
    /[&<>"']/g,
    (char) =>
      ({
        "&": "&amp;",
        "<": "&lt;",
        ">": "&gt;",
        '"': "&quot;",
        "'": "&apos;",
      })[char]
  )

const withPngCaption = (qrCanvas, caption) => {
  // The QR canvas is drawn at the device pixel ratio, so read its real size.
  const width = qrCanvas.width
  const fontSize = Math.round(width * CAPTION_RATIO)
  const output = document.createElement("canvas")
  output.width = width
  output.height = width + Math.round(fontSize * 1.75)

  const context = output.getContext("2d")
  context.fillStyle = "#ffffff"
  context.fillRect(0, 0, output.width, output.height)
  context.drawImage(qrCanvas, 0, 0)

  context.font = `${fontSize}px ${CAPTION_FONT}`
  context.fillStyle = CAPTION_COLOR
  context.textAlign = "center"
  context.textBaseline = "top"
  context.fillText(fitCaption(context, caption, width * 0.9), width / 2, width)

  return output
}

const withSvgCaption = (qrSvg, caption) => {
  const width = Number(qrSvg.getAttribute("width"))
  const fontSize = Math.round(width * CAPTION_RATIO * 10) / 10
  const height = width + fontSize * 1.75
  const text = fitCaption(captionContext(fontSize), caption, width * 0.9)

  return `<svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${height}" viewBox="0 0 ${width} ${height}">
<rect width="100%" height="100%" fill="#ffffff"/>
${new XMLSerializer().serializeToString(qrSvg)}
<text x="${width / 2}" y="${width + fontSize}" text-anchor="middle" font-family="${CAPTION_FONT}" font-size="${fontSize}" fill="${CAPTION_COLOR}">${escapeXml(text)}</text>
</svg>`
}

const toFileName = (eventName) =>
  `${(eventName || "event")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "")}-qr`

const download = (href, fileName) => {
  const link = document.createElement("a")
  link.href = href
  link.download = fileName
  link.click()
}

/*
  An event's QR, drawn from its PublicUuid every time it is shown — nothing is
  stored. PublicUuid never changes, so this is always the same image as the one
  already printed, however often the event is edited.
*/
const EventQrPreview = ({ publicUuid, eventName, size = 200 }) => {
  const svgWrapperRef = useRef(null)
  const canvasWrapperRef = useRef(null)
  const link = getEventQrLink(publicUuid)
  const fileName = toFileName(eventName)

  const caption = eventName || ""

  const handleDownloadPng = () => {
    const canvas = canvasWrapperRef.current?.querySelector("canvas")
    if (!canvas) return
    const image = caption ? withPngCaption(canvas, caption) : canvas
    download(image.toDataURL("image/png"), `${fileName}.png`)
  }

  const handleDownloadSvg = () => {
    const svg = svgWrapperRef.current?.querySelector("svg")
    if (!svg) return
    const markup = caption
      ? withSvgCaption(svg, caption)
      : new XMLSerializer().serializeToString(svg)
    const url = URL.createObjectURL(
      new Blob([markup], { type: "image/svg+xml" })
    )
    download(url, `${fileName}.svg`)
    URL.revokeObjectURL(url)
  }

  return (
    <div className="flex flex-col items-center gap-3">
      <div
        ref={svgWrapperRef}
        className="rounded-xl border border-gray-200 bg-white p-3"
      >
        <QRCodeSVG
          level="M"
          marginSize={2}
          size={size}
          title={eventName}
          value={link}
        />
        {/* w-0 + min-w-full: as wide as the QR, never wider, so a long name
            truncates instead of stretching the box. */}
        {caption && (
          <p
            className="w-0 min-w-full truncate text-center text-[10px] leading-tight text-gray-500"
            title={caption}
          >
            {caption}
          </p>
        )}
      </div>

      {/* Off-screen, high-resolution copy used only for the PNG download. */}
      <div ref={canvasWrapperRef} className="hidden">
        <QRCodeCanvas level="M" marginSize={4} size={PNG_SIZE} value={link} />
      </div>

      <div className="flex w-full items-center gap-1 rounded-lg bg-gray-50 border border-gray-200 pl-2">
        <span className="truncate text-xs text-gray-600" title={link}>
          {link}
        </span>
        <span className="ml-auto shrink-0 text-gray-500">
          <CopyIcon value={link} />
        </span>
      </div>

      <div className="flex w-full flex-wrap gap-2">
        <button
          className="flex flex-1 items-center justify-center gap-2 rounded-lg bg-secondary px-3 py-2 text-xs font-medium text-white hover:bg-secondary_dark"
          type="button"
          onClick={handleDownloadPng}
        >
          <FontAwesomeIcon className="w-3 h-3" icon={faDownload} />
          PNG
        </button>
        <button
          className="flex flex-1 items-center justify-center gap-2 rounded-lg border border-slate-300 px-3 py-2 text-xs font-medium text-gray-700 hover:bg-slate-50"
          type="button"
          onClick={handleDownloadSvg}
        >
          <FontAwesomeIcon className="w-3 h-3" icon={faDownload} />
          SVG
        </button>
        <a
          className="flex flex-1 items-center justify-center gap-2 rounded-lg border border-slate-300 px-3 py-2 text-xs font-medium text-gray-700 hover:bg-slate-50"
          href={getEventQrPreviewLink(publicUuid)}
          rel="noreferrer"
          target="_blank"
        >
          <FontAwesomeIcon className="w-3 h-3" icon={faEye} />
          Preview
        </a>
      </div>

      <p className="text-[11px] text-gray-400 text-center">
        Editing the event&apos;s details does not change this QR code.
      </p>
    </div>
  )
}

export default EventQrPreview
